import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  LuHouse, LuLayoutDashboard, LuUsers, LuListOrdered, LuCalendar,
  LuCircleHelp, LuScissors, LuStore, LuLogOut, LuDollarSign, LuSettings,
  LuClock, LuCoins
} from "react-icons/lu";
import { useNavigate, useLocation } from "react-router-dom";
import { useBarbeiro } from "../context/BarbeiroContext";
import { getAuth, signOut } from "firebase/auth";
import { app } from "../firebase";

// === Constantes para Dimensões ===
const ITEM_HEIGHT = 48; 
const ITEM_GAP = 4;     

// === Styled Components ===
const SidebarWrapper = styled.div`
  border-right: 1px solid #e5e7eb;
`;

const Sidebar = styled.aside`
  width: 240px;
  height: 100vh;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.5rem 1rem;
`;

const MenuGroup = styled.div`
  position: relative; 
  display: flex;
  flex-direction: column;
  gap: ${ITEM_GAP}px;
`;

// O Fundo Azul Deslizante (Só para o menu principal)
const ActiveIndicator = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: ${ITEM_HEIGHT}px;
  background-color: #3a79ed;
  border-radius: 0.75rem;
  box-shadow: 0 4px 12px rgba(58, 136, 237, 0.3);
  z-index: 0;
  transition: transform 0.3s cubic-bezier(0.25, 0.1, 0.25, 1), opacity 0.2s;
  
  /* Esconde se não tiver item ativo no grupo */
  opacity: ${(props) => (props.visible ? 1 : 0)};
  transform: translateY(${(props) => props.offset}px);
`;

const MenuItem = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 1rem;
  height: ${ITEM_HEIGHT}px;
  padding: 0 1rem;
  border-radius: 0.75rem;
  font-size: 0.95rem;
  
  /* --- LÓGICA DE COR DO FUNDO --- */
  /* Se for estático (Ajuda) e ativo -> AZUL. 
     Se for do menu principal -> Transparente (pois o slider está atrás) */
  background-color: ${(props) => 
    props.isStatic && props.active ? "#3a79ed" : "transparent"};

  /* Sombra apenas se for estático e ativo */
  box-shadow: ${(props) => 
    props.isStatic && props.active ? "0 4px 12px rgba(58, 136, 237, 0.3)" : "none"};
  
  color: ${(props) => (props.active ? "#fff" : "#4b5563")};
  font-weight: ${(props) => (props.active ? "600" : "500")};
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    /* Só mostra cinza se inativo. Se ativo (seja slider ou estático), mantém a cor correta */
    background-color: ${(props) => {
        if (props.isStatic && props.active) return "#3a79ed"; // Mantém azul no hover
        if (props.active) return "transparent"; // Mantém transparente no hover (slider atrás)
        return "#f3f4f6"; // Cinza se inativo
    }};
  }
`;

const Divider = styled.hr`
  margin: 1.5rem 0;
  border: none;
  border-top: 1px solid #e5e7eb;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const Avatar = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 12px;
  object-fit: cover;
`;

const AvatarDetails = styled.div`
  color: #4b5563;
  display: flex;
  flex-direction: column;

  div:first-child {
    font-weight: 600;
    font-size: 0.875rem;
  }

  div:last-child {
    font-size: 0.75rem;
    color: #6b7280;
  }
`;

const AvatarWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 0.75rem;
  border-radius: 0.75rem;
  background-color: #f9fafb;
  transition: background 0.2s;
  cursor: default;

  &:hover {
    background-color: #f3f4f6;
  }
`;

const LogoutButton = styled.div`
  color: #757575;
  padding: 0.4rem;
  border-radius: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 0.3s ease;
  margin-left: 0.75rem;

  &:hover {
    background-color: #f3f4f6;
  }
`;

export default function SidebarCliente() {
  const auth = getAuth(app);
  const navigate = useNavigate();
  const location = useLocation();
  const { barbeiro } = useBarbeiro();

  const FOTO_PADRAO = "https://firebasestorage.googleapis.com/v0/b/bigode-app-fade.firebasestorage.app/o/padrao-sistema%2Fbarbeiro.webp?alt=media&token=732c1f69-8182-414d-8237-ac0f509bf0fd";

  const [nomeLocal, setNomeLocal] = useState(barbeiro?.nome || "Barbeiro");
  const [fotoLocal, setFotoLocal] = useState(barbeiro?.foto || FOTO_PADRAO);
  const [emailLocal, setEmailLocal] = useState(barbeiro?.email || "");

  useEffect(() => {
    if (barbeiro) {
      setNomeLocal(barbeiro.nome || "Barbeiro");
      setFotoLocal(barbeiro.foto || FOTO_PADRAO);
      setEmailLocal(barbeiro.email || "");
    }
  }, [barbeiro]);

  useEffect(() => {
    const onProfileUpdate = (e) => {
      const { displayName, foto } = e.detail || {};
      if (displayName != null) setNomeLocal(displayName);
      if (foto != null) setFotoLocal(foto || FOTO_PADRAO);
    };
    window.addEventListener("bigode:profile-updated", onProfileUpdate);
    return () => window.removeEventListener("bigode:profile-updated", onProfileUpdate);
  }, []);

  const isBarbeiro = barbeiro?.cargo === "barbeiro";

  const MENU_ITEMS = isBarbeiro
    ? [
      { key: "home", label: "Pág. Inicial", icon: <LuHouse size={20} />, path: "/" },
      { key: "agenda", label: "Agenda", icon: <LuCalendar size={20} />, path: "/agenda-barbeiro" },
      { key: "fila", label: "Fila de Espera", icon: <LuClock size={20} />, path: "/fila" },
      { key: "comissao", label: "Comissão", icon: <LuDollarSign size={20} />, path: "/comissao" },
    ]
    : [
      { key: "home", label: "Pág. Inicial", icon: <LuHouse size={20} />, path: "/" },
      { key: "agenda", label: "Agenda", icon: <LuCalendar size={20} />, path: "/agenda-horarios" },
      { key: "fila", label: "Fila de Espera", icon: <LuClock size={20} />, path: "/fila" },
      { key: "financeiro", label: "Financeiro", icon: <LuCoins size={20} />, path: "/financeiro" },
      { key: "servicos", label: "Serviços", icon: <LuScissors size={20} />, path: "/servicos" },
      { key: "equipe", label: "Equipe", icon: <LuUsers size={20} />, path: "/equipe" },
      { key: "detalhamento", label: "Detalhamento", icon: <LuListOrdered size={20} />, path: "/detalhamento" },
      { key: "investimentos", label: "Minha Barbearia", icon: <LuStore size={20} />, path: "/minha-barbearia" },
      { key: "configuracoes", label: "Configurações", icon: <LuSettings size={20} />, path: "/configuracoes" },
    ];

  const MENU_BOTTOM = [
    { key: "ajuda", label: "Ajuda", icon: <LuCircleHelp size={20} />, path: "/ajuda" },
  ];

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/login";
  };

  // === LÓGICA DA ANIMAÇÃO ===
  const activeIndex = MENU_ITEMS.findIndex(item => item.path === location.pathname);
  const activeOffset = activeIndex !== -1 ? activeIndex * (ITEM_HEIGHT + ITEM_GAP) : 0;
  const isMainMenuActive = activeIndex !== -1;

  return (
    <SidebarWrapper>
      <Sidebar>
        <div>
          <img style={{ width: '80%', marginBottom: '1rem', padding: '1rem' }} src="https://bigode.app/assets/logo2.png" alt="Logo" />
          
          <MenuGroup>
            {/* O Fundo Azul Deslizante */}
            <ActiveIndicator 
              offset={activeOffset} 
              visible={isMainMenuActive} 
            />

            {MENU_ITEMS.map((item) => (
              <MenuItem
                key={item.key}
                active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
              >
                <span style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {item.icon} {item.label}
                </span>
              </MenuItem>
            ))}
          </MenuGroup>

          <Divider />

          {/* Menu inferior (Ajuda) com prop isStatic={true} */}
          <MenuGroup>
            {MENU_BOTTOM.map((item) => (
              <MenuItem
                key={item.key}
                active={location.pathname === item.path}
                onClick={() => navigate(item.path)}
                isStatic={true} /* <--- AQUI ESTÁ O TRUQUE */
              >
                {item.icon} {item.label}
              </MenuItem>
            ))}
          </MenuGroup>
        </div>

        <BottomSection>
          <AvatarWrapper>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Avatar src={fotoLocal} alt="Avatar" />
              <AvatarDetails>
                <div>
                  {(() => {
                    const primeiroNome = (nomeLocal || "Barbeiro").split(" ")[0];
                    return primeiroNome.length > 11 ? primeiroNome.slice(0, 11) + "..." : primeiroNome;
                  })()}
                </div>
                <div>
                  {emailLocal?.length > 12 ? emailLocal.slice(0, 12) + "..." : emailLocal}
                </div>
              </AvatarDetails>
            </div>
            <LogoutButton onClick={handleLogout} title="Sair">
              <LuLogOut size={14} />
            </LogoutButton>
          </AvatarWrapper>
        </BottomSection>
      </Sidebar>
    </SidebarWrapper>
  );
}